import React, { useContext, useState } from "react";
import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
  styled,
} from "@mui/material";

import { useFormik } from "formik";

// img
import ManBoxes from "../assets/images/login.Img/ManBoxes.png";
import logo from "../assets/images/newlogogms.png";
import ThemeModeContext from "../context/ThemeModeContext";
import UserContext from "../context/UserContext";
import { useNavigate } from "react-router-dom";
import FormInput from "../forms/FormInput";
import OtpInput from "../components/OtpInput";
import { post } from "../services/apiMethods";
import URLS from "../services/urlConstants";
import { jwtDecode } from "jwt-decode";
import { LoadingButton } from "@mui/lab";
import {
  ArrowBack,
  LockOutlined,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { getDefaultRoute } from "../data/menu";

const LeftComponent = styled(Grid)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
});

const RightComponent = styled(Grid)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
});

const Item = styled(Paper)(() => ({
  display: "flex",
  flexDirection: "column",
  textAlign: "center",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "16px",
  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
}));

const MuiForm = styled(Box)({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
});

const MuiButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(3),
  padding: theme.spacing(1),
  borderRadius: "24px",
  background: "linear-gradient(135deg, #196838, #446285)",
  color: "#fff",
  fontWeight: "bold",
  fontSize: "16px",
  "&:hover": {
    background: "linear-gradient(135deg, #144d2b, #3a5173)",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.3)",
  },
}));

export default function Login() {
  const { setUser } = useContext(UserContext);
  const { isDarkMode } = useContext(ThemeModeContext);
  const navigate = useNavigate();
  const [password, setPassword] = useState(false);
  const [logging, setLogging] = useState(false);

  /* Second step state - set once the password is accepted but 2FA is on */
  const [challenge, setChallenge] = useState(null);
  const [otp, setOtp] = useState("");
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [backupCode, setBackupCode] = useState("");
  const [verifying, setVerifying] = useState(false);

  /* Store the session and land the user on their default page */
  const completeLogin = (result) => {
    const decodedToken = jwtDecode(result.access_token);

    setUser(result.user);
    window.localStorage.setItem(
      "current_inventory_user",
      JSON.stringify(result.user)
    );

    let landingRoute = "/dashboard";

    if (decodedToken.data.userType === "employee") {
      const role = result.user.role;
      window.localStorage.setItem("role", JSON.stringify(role));
      landingRoute = getDefaultRoute(role?.permissions);
    }

    window.localStorage.setItem("access_token", result.access_token);
    navigate(landingRoute);
  };

  const handleLogin = async (values) => {
    try {
      setLogging(true);
      const { data } = await post(URLS.auth.login, values);

      /* Authenticator app is enabled - no token yet, ask for the code */
      if (data.result?.twoFactorRequired) {
        setChallenge(data.result);
        setOtp("");
        setBackupCode("");
        setUseBackupCode(false);
        setLogging(false);
        return;
      }

      completeLogin(data.result);
      setLogging(false);
    } catch (error) {
      console.log(error);
    }
    setLogging(false);
  };

  const handleVerifyOtp = async (codeOverride) => {
    const code = codeOverride || otp;
    if (!useBackupCode && code.length !== 6) return;
    if (useBackupCode && !backupCode.trim()) return;

    try {
      setVerifying(true);
      const payload = {
        twoFactorToken: challenge.twoFactorToken,
        ...(useBackupCode
          ? { backupCode: backupCode.trim() }
          : { token: code }),
      };
      const { data } = await post(URLS.auth.loginTwoFactor, payload);
      completeLogin(data.result);
    } catch (error) {
      console.log(error);
      setOtp("");
      /* The verification window is short - send the user back to re-enter the password */
      if (error?.response?.status === 401) {
        setChallenge(null);
      }
    }
    setVerifying(false);
  };

  const handleBackToPassword = () => {
    setChallenge(null);
    setOtp("");
    setBackupCode("");
    setUseBackupCode(false);
  };

  const formik = useFormik({
    initialValues: {
      userName: "",
      password: "",
    },
    onSubmit: async (values) => {
      handleLogin(values);
    },
    validateOnBlur: false,
  });

  const togglePasswordVisibility = () => {
    setPassword((prev) => !prev);
  };

  const renderPasswordStep = () => (
    <MuiForm
      component="form"
      onSubmit={formik.handleSubmit}
      sx={{
        maxWidth: { xs: "100%", sm: 450, md: 460, lg: 650 },
        width: "100%",
        padding: { xs: "50px", md: "60px" },
      }}
    >
      <img
        src={logo}
        alt="loading.."
        width={250}
        style={{ marginBottom: "20px", objectFit: "contain" }}
      />

      <Typography
        fontWeight="bold"
        sx={{
          textAlign: "left",
          width: "100%",
          mb: 1,
          fontSize: "20px",
        }}
      >
        Welcome Back!
      </Typography>

      <Typography
        color="secondary"
        sx={{ textAlign: "left", width: "100%", mb: 3 }}
      >
        Please enter your details
      </Typography>

      <FormInput
        formik={formik}
        sx={{ mb: 1 }}
        name="userName"
        label="Username"
        type="text"
        required={true}
      />
      <FormInput
        formik={formik}
        name="password"
        label="Password"
        type={password ? "text" : "password"}
        required={true}
        inputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={togglePasswordVisibility}
                size="small"
                edge="end"
                sx={{ padding: 0 }}
              >
                {password ? (
                  <VisibilityOff fontSize="small" sx={{ color: "grey" }} />
                ) : (
                  <Visibility fontSize="small" sx={{ color: "grey" }} />
                )}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <LoadingButton
        loading={logging}
        type="submit"
        size="small"
        fullWidth
        variant="contained"
        sx={{ mt: 4, height: 45 }}
      >
        Login
      </LoadingButton>
    </MuiForm>
  );

  const renderOtpStep = () => (
    <MuiForm
      component="form"
      onSubmit={(e) => {
        e.preventDefault();
        handleVerifyOtp();
      }}
      sx={{
        maxWidth: { xs: "100%", sm: 450, md: 460, lg: 650 },
        width: "100%",
        padding: { xs: "40px", md: "60px" },
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #196838, #446285)",
          mb: 2,
        }}
      >
        <LockOutlined sx={{ color: "#fff", fontSize: 28 }} />
      </Box>

      <Typography fontWeight="bold" sx={{ fontSize: "20px", mb: 1 }}>
        Two-Step Verification
      </Typography>

      <Typography
        color="secondary"
        sx={{ textAlign: "center", mb: 3, fontSize: "14px" }}
      >
        {useBackupCode
          ? "Enter one of the backup codes you saved when you set up two-step verification."
          : `Enter the 6-digit code from your authenticator app${
              challenge?.name ? ` to continue as ${challenge.name}` : ""
            }.`}
      </Typography>

      {useBackupCode ? (
        <TextField
          fullWidth
          autoFocus
          label="Backup code"
          placeholder="xxxxx-xxxxx"
          value={backupCode}
          onChange={(e) => setBackupCode(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ autoComplete: "off", maxLength: 11 }}
        />
      ) : (
        <OtpInput
          value={otp}
          onChange={setOtp}
          onComplete={(code) => handleVerifyOtp(code)}
          disabled={verifying}
        />
      )}

      <LoadingButton
        loading={verifying}
        type="submit"
        size="small"
        fullWidth
        variant="contained"
        disabled={useBackupCode ? !backupCode.trim() : otp.length !== 6}
        sx={{ mt: 4, height: 45 }}
      >
        Verify
      </LoadingButton>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ width: "100%", mt: 2 }}
      >
        <Button
          size="small"
          startIcon={<ArrowBack fontSize="small" />}
          onClick={handleBackToPassword}
          sx={{ textTransform: "none" }}
        >
          Back
        </Button>

        <Link
          component="button"
          type="button"
          underline="hover"
          onClick={() => {
            setUseBackupCode((prev) => !prev);
            setOtp("");
            setBackupCode("");
          }}
          sx={{ fontSize: "13px" }}
        >
          {useBackupCode
            ? "Use authenticator app instead"
            : "Use a backup code"}
        </Link>
      </Stack>

      {!useBackupCode && challenge?.backupCodesRemaining === 0 && (
        <Typography
          sx={{ fontSize: "12px", color: "text.secondary", mt: 2 }}
          textAlign="center"
        >
          You have no backup codes left. Generate a new set from Settings after
          signing in.
        </Typography>
      )}
    </MuiForm>
  );

  return (
    <>
      <Grid container>
        <LeftComponent
          item
          xs={12}
          md={6}
          sx={{
            display: { xs: "none", md: "flex" },
            height: "100vh",
            background: "linear-gradient(135deg, #196838, #446285)",
          }}
        >
          <img src={ManBoxes} alt="loading..." />
        </LeftComponent>

        <RightComponent
          item
          xs={12}
          md={6}
          sx={{
            height: "100vh",
            backgroundColor: isDarkMode ? "black" : "white",
          }}
        >
          <Item
            elevation={1}
            sx={{
              width: "100%",
              maxWidth: "500px",
              marginRight: "15px",
              marginLeft: "15px",
              justifyContent: "center",
            }}
          >
            {challenge ? renderOtpStep() : renderPasswordStep()}
          </Item>
        </RightComponent>
      </Grid>
    </>
  );
}
